import React, { useEffect, useMemo, useState } from 'react';
import Axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import I18n from '@/modules/i18n';
import * as Routes from '@/modules/routes';
import RoutedSPADialog from '@/components/admin/SysadminSPA/shared/RoutedSPADialog';
import SPADialog from '@/components/admin/SysadminSPA/shared/SPADialog';
import SPASelect from '@/components/admin/SysadminSPA/shared/SPASelect';
import usePrograms from '@/components/admin/ProgramManagement/usePrograms';
import { useContentMount } from './contentMount';
import { programEditPath } from './ProgramEdit/editSections';

type ISelectOption = {
  value: number;
  label: string;
};

type IProgram = {
  id: number;
  code: string;
  full_title_with_edition: string;
  kind: string;
};

const formatOptions = (programs: IProgram[]) => programs.map(program => ({
  kind: program.kind,
  label: `[${program.code}] ${program.full_title_with_edition}`,
  value: program.id,
})).filter(program => program.kind !== 'professional_development');

// AIDEV-NOTE: SPA-specific modal for reusing an existing program.
// Mounted as a child route (/content/reuse-program) via RoutedSPADialog.
// The "create new" path is handled by the dropdown in ProgramsTable — it
// navigates directly, so this modal only handles the select-and-clone flow.
// The shared AddProgramModal in ProgramManagement/ is still used by the legacy page.
const SPAAddProgramModal = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState<ISelectOption | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mount = useContentMount();

  const { programs, isLoading: isLoadingPrograms } = usePrograms({
    attributes: ['full_title_with_edition', 'id', 'code', 'kind'],
  });
  const options = useMemo(() => formatOptions(programs), [programs]);

  // Deep link support: /content/reuse-program?base_program_id=123 pre-selects that program.
  useEffect(() => {
    const baseProgramId = Number(searchParams.get('base_program_id'));
    if (!baseProgramId) return;

    const match = options.find(option => option.value === baseProgramId);
    if (match) setSelected(match);
  }, [options, searchParams]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    Axios.post(
      Routes.plato_api_programs_path({ base_program_id: selected?.value }),
    ).then((res) => {
      navigate(programEditPath(res.data.data.id, mount));
    }).catch((err) => {
      console.error('error', err);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  return (
    <RoutedSPADialog>
      <SPADialog.Content className="tw-overflow-visible" size="sm">
        <SPADialog.Header>
          <SPADialog.Title>Create Program from Existing</SPADialog.Title>
          <SPADialog.Description className="tw-mt-1 tw-text-spa-secondary">
            The new program starts as a copy of the base program you select.
          </SPADialog.Description>
          <SPADialog.CloseButton />
        </SPADialog.Header>
        <form
          autoComplete="off"
          className="tw-flex tw-flex-col tw-flex-1"
          onSubmit={handleSubmit}
        >
          <SPADialog.Body className="tw-overflow-visible">
            <label
              className="tw-block"
              htmlFor="base_program_id"
            >
              <span className="tw-text-sm tw-font-bold tw-text-spa-heading tw-mb-1.5 tw-block">
                Base Program
              </span>
              <SPASelect
                isClearable
                inputId="base_program_id"
                isLoading={isLoadingPrograms}
                name="base_program_id"
                options={options}
                placeholder="Search programs..."
                value={selected}
                onChange={setSelected}
              />
            </label>
          </SPADialog.Body>
          <SPADialog.Footer>
            <SPADialog.Close asChild>
              <SPADialog.Button>{I18n.t('cancel')}</SPADialog.Button>
            </SPADialog.Close>
            <SPADialog.Button
              disabled={!selected || isLoading}
              type="submit"
              variant="primary"
            >
              {isLoading ? 'Creating Program…' : 'Create Program'}
            </SPADialog.Button>
          </SPADialog.Footer>
        </form>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default SPAAddProgramModal;
